import { useState, useEffect, useCallback } from 'react';
import { auth } from '@/lib/picasso-firebase';
import useSharedWebSocket from './useSharedWebSocket';

// Unified notification interface
interface Notification {
  id: string;
  message: string;
  event: string;
  status: 'read' | 'unread';
  created_at: string;
  read_at?: string;
  video_id?: string;
  user_email?: string;
  data?: any;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  lastNotification: Notification | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => Promise<void>;
  isConnected: boolean;
  refreshNotifications: () => Promise<void>;
}

// Helper function to generate default notification messages
const getDefaultNotificationMessage = (event: string, data: any): string => {
  const title = data?.title;
  const status = data?.status;
  
  // If we don't have essential data, return empty string to skip notification
  if (!title && !status) {
    return '';
  }
  
  const videoTitle = title || 'video';
  
  switch (event) {
    case 'video_generated':
      return `Video "${videoTitle}" has been generated`;
    case 'video_uploaded':
      return `Video "${videoTitle}" has been uploaded`;
    case 'video_updated':
      if (status) {
        return `Video "${videoTitle}" status updated to ${status}`;
      }
      return `Video "${videoTitle}" was updated`;
    case 'video_deleted':
      return `Video "${videoTitle}" was deleted`;
    case 'delete_request':
      return `Delete request for video "${videoTitle}"`;
    case 'admin_notification':
      return `Admin notification for video "${videoTitle}"`;
    case 'video_approved':
      return `Video "${videoTitle}" was approved`;
    case 'video_rejected':
      return `Video "${videoTitle}" was rejected`;
    case 'new_video_submission':
      return `New video submission: "${videoTitle}"`;
    default:
      return ''; // Return empty string for unknown events
  }
};

const useNotifications = (
  userEmail: string | null,
  isAdmin: boolean
): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastNotification, setLastNotification] = useState<Notification | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_BASE_URL;

  // Helper function to clean notifications
  const cleanNotifications = useCallback((notifs: Notification[]) => {
    return notifs.filter(notif => 
      notif.message && 
      notif.message.trim() !== '' && 
      notif.message !== 'Video echo' &&
      !notif.message.includes('undefined') &&
      !notif.message.includes('null') &&
      notif.event &&
      notif.id
    );
  }, []);

  // Clean notifications periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setNotifications(prev => {
        const cleaned = cleanNotifications(prev);
        if (cleaned.length !== prev.length) {
          return cleaned;
        }
        return prev;
      });
    }, 5000); // Clean every 5 seconds

    return () => clearInterval(interval);
  }, [cleanNotifications]);

  // API call to get notifications from backend
  const refreshNotifications = useCallback(async () => {
    if (!userEmail || !auth.currentUser) return;
    
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/picasso/notifications/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Filter out any notifications without proper messages or invalid content
          const validNotifications = (data.notifications || []).filter((notif: Notification) => {
            return notif.message && 
                   notif.message.trim() !== '' && 
                   notif.message !== 'Video undefined' &&
                   notif.message !== 'Video echo' &&
                   !notif.message.includes('undefined') &&
                   !notif.message.includes('null') &&
                   notif.event &&
                   notif.id;
          });
          setNotifications(validNotifications);
          setUnreadCount(data.unread_count || 0);
        }
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [userEmail]);

  // Mark specific notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!userEmail || !auth.currentUser) return;
    
    try {
      const token = await auth.currentUser.getIdToken();
      const formData = new FormData();
      formData.append('notification_id', notificationId);

      const response = await fetch(`${API_BASE_URL}/picasso/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update local state
          setNotifications(prev => 
            prev.map(notif => 
              notif.id === notificationId 
                ? { ...notif, status: 'read', read_at: new Date().toISOString() }
                : notif
            )
          );
          setUnreadCount(data.unread_count || 0);
        }
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [userEmail]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!userEmail || !auth.currentUser) return;
    
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/picasso/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update local state
          setNotifications(prev => 
            prev.map(notif => ({ 
              ...notif, 
              status: 'read', 
              read_at: new Date().toISOString() 
            }))
          );
          setUnreadCount(0);
        }
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [userEmail]);

  // Clear all notifications
  const clearAllNotifications = useCallback(async () => {
    if (!userEmail || !auth.currentUser) return;
    
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/picasso/notifications/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setNotifications([]);
          setUnreadCount(0);
        }
      }
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  }, [userEmail]);

  // Remove notification from local state (for UI purposes)
  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
    setUnreadCount(prev => {
      const notification = notifications.find(n => n.id === id);
      return notification?.status === 'unread' ? prev - 1 : prev;
    });
  }, [notifications]);

  // Handle WebSocket messages for real-time notifications
  const handleWebSocketMessage = useCallback((data: any) => {
    if (!userEmail) return;

    try {
      const isUserNotification = data.room_type === 'user';
      const isAdminNotification = data.room_type === 'admin' && isAdmin;
      
      // Only process notifications that have proper event types and are meant for notification bell
      const validNotificationEvents = [
        'video_generated', 
        'video_uploaded', 
        'video_updated', 
        'video_deleted', 
        'delete_request',
        'admin_notification',
        'video_approved',
        'video_rejected',
        'new_video_submission'
      ];
      
      if (data.event && 
          data.data && 
          validNotificationEvents.includes(data.event) &&
          (isUserNotification || isAdminNotification)) {
        
        // Skip notification if this admin is excluded (they made the update)
        if (isAdminNotification && data.data.exclude_admin && data.data.exclude_admin === userEmail) {
          return;
        }

        // Generate a proper message - if no message, skip the notification entirely
        const message = data.data.message || getDefaultNotificationMessage(data.event, data.data);
        
        // Don't create notification if we can't generate a meaningful message
        if (!message || 
            message.trim() === '' || 
            message === 'Video undefined' || 
            message === 'Video "video"' ||
            message.includes('undefined') ||
            message.includes('null')) {
          return;
        }

        // Additional validation for essential data
        if (!data.data.video_id && !data.data.title) {
          return;
        }

        const notification: Notification = {
          id: `notif_${Date.now()}_${crypto.randomUUID()}`,
          message: message,
          event: data.event,
          status: 'unread',
          created_at: data.timestamp || new Date().toISOString(),
          video_id: data.data.video_id,
          user_email: data.data.user_email || userEmail,
          data: data.data || {}
        };

        // Final validation before adding to state
        if (!notification.message || 
            notification.message.trim() === '' ||
            notification.message === 'Video echo' ||
            !notification.event ||
            !notification.id) {
          return;
        }

        // Add notification to local state
        setNotifications(prev => [notification, ...prev]);
        setLastNotification(notification);
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error processing WebSocket notification:', error);
    }
  }, [userEmail, isAdmin]);

  // WebSocket connection
  const { isConnected: wsConnected } = useSharedWebSocket(
    'notifications',
    handleWebSocketMessage
  );

  // Update connection status
  useEffect(() => {
    setIsConnected(wsConnected);
  }, [wsConnected]);

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    if (userEmail) {
      // Clear any existing frontend state first to prevent stale data
      setNotifications([]);
      setUnreadCount(0);
      setLastNotification(null);
      
      // Then refresh from backend (don't clear backend notifications on page load)
      refreshNotifications();
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
      setUnreadCount(0);
      setLastNotification(null);
    }
  }, [userEmail, isAdmin, refreshNotifications]); // Removed clearAllNotifications dependency

  return {
    notifications,
    unreadCount,
    lastNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
    isConnected,
    refreshNotifications
  };
};

export default useNotifications;
